import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { X, Truck, MapPin, MagnifyingGlass, Check } from "@phosphor-icons/react";

const inp = "w-full h-9 px-3 border border-border-control bg-white text-[13.5px] rounded-[8px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none focus:border-equipr-black";
const lbl = "text-[10.5px] font-bold uppercase tracking-[0.1em] text-ink-muted mb-1.5 block";

export default function OrganiseTransportModal({ prefill, onClose, onCreated }) {
  const today = new Date().toISOString().slice(0, 10);
  const [assets, setAssets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [sites, setSites] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(prefill?.asset_nos || []);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    from_type: prefill?.from_type || "project", from_id: prefill?.from_id || "",
    to_type: prefill?.to_type || "site", to_id: prefill?.to_id || "",
    scheduled_at: today, on_hire_date: today, notes: "", carrier: "",
  });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    Promise.all([api.get("/assets"), api.get("/projects"), api.get("/sites"), api.get("/suppliers")]).then(([a, p, s, sup]) => {
      setAssets(a.data);
      setProjects(p.data.map((x) => ({ id: x.id, name: x.project_name || x.site_address || x.project_no || "Project" })));
      setSites(s.data.map((x) => ({ id: x.id, name: `${x.name}${x.site_type ? ` (${x.site_type})` : ""}` })));
      setCarriers(sup.data.filter((x) => x.is_transport_provider).map((x) => x.name));
    }).catch(() => {});
  }, []);

  const filtered = useMemo(() => assets.filter((a) => {
    const t = q.toLowerCase();
    return !t || (a.asset_no || "").toLowerCase().includes(t) || (a.asset_type || "").toLowerCase().includes(t) || (a.last_location || "").toLowerCase().includes(t);
  }), [assets, q]);

  const toggle = (assetNo) => setSelected((s) => s.includes(assetNo) ? s.filter((x) => x !== assetNo) : [...s, assetNo]);

  const locName = (type, id) => (type === "project" ? projects : sites).find((d) => d.id === id)?.name || "";

  const submit = async () => {
    setError("");
    if (selected.length === 0) { setError("Select at least one asset to move."); return; }
    if (!form.to_id) { setError("Choose a destination."); return; }
    setSaving(true);
    try {
      await api.post("/transportation", {
        asset_nos: selected,
        project_id: form.from_type === "project" ? form.from_id : (prefill?.project_id || null),
        from_type: form.from_type, from_id: form.from_id, from_location: locName(form.from_type, form.from_id) || (prefill?.from_location || ""),
        to_type: form.to_type, to_id: form.to_id, to_location: locName(form.to_type, form.to_id),
        scheduled_at: form.scheduled_at, on_hire_date: form.to_type === "project" ? form.on_hire_date : null,
        status: "scheduled", notes: form.notes, carrier: form.carrier,
      });
      onCreated?.();
      onClose();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not create transport.");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[rgba(26,26,26,0.42)] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-surface-card border border-border-warm rounded-[18px] overflow-hidden shadow-modal w-full max-w-[680px] max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="organise-transport-modal">
        <div className="px-6 pt-5 pb-4 border-b border-divider-inner shrink-0 flex items-start justify-between">
          <div>
            <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-ink-muted">Transport</div>
            <h3 className="text-[24px] font-bold text-ink-primary leading-tight mt-0.5">Organise a movement</h3>
          </div>
          <button onClick={onClose} data-testid="organise-close" className="w-9 h-9 flex items-center justify-center rounded-[9px] border border-border-control text-ink-secondary hover:bg-surface-panel"><X size={16} weight="bold" /></button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto bg-surface-inset">
          {error && <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-[8px]" data-testid="organise-error">{error}</div>}

          <section className="bg-surface-card border border-border rounded-[12px] overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-divider-inner">
              <h4 className="font-bold text-[15px] text-ink-primary whitespace-nowrap">Assets to move <span className="font-normal text-[12px] text-ink-tertiary ml-1">{selected.length ? `${selected.length} picked` : "No assets picked"}</span></h4>
              <div className="relative w-64 max-w-[55%]">
                <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Unit #, type or current site" data-testid="organise-asset-search" className="w-full h-9 pl-8 pr-2 border border-border-control bg-white text-[13px] rounded-[8px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none" />
              </div>
            </div>
            <div className="max-h-56 overflow-y-auto">
              {filtered.length === 0 ? <div className="p-4 text-center text-[13px] text-ink-tertiary">No assets.</div> :
                filtered.map((a) => {
                  const on = selected.includes(a.asset_no);
                  return (
                    <button key={a.id} type="button" onClick={() => toggle(a.asset_no)} data-testid={`organise-asset-${a.asset_no}`}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left border-b border-divider-row last:border-0 hover:bg-accent-wash ${on ? "bg-accent-tint" : ""}`}>
                      <span className={`w-4 h-4 rounded-[4px] border flex items-center justify-center shrink-0 ${on ? "bg-equipr-yellow border-accent-edge" : "border-border-control bg-white"}`}>{on && <Check size={11} weight="bold" className="text-black" />}</span>
                      <span className="text-[14px] font-mono font-semibold text-ink-primary">{a.asset_no}</span>
                      <span className="text-[13px] text-ink-secondary">{a.asset_type}</span>
                      <span className="text-[13px] text-ink-tertiary ml-auto truncate">at {a.last_location || "Depot"}</span>
                    </button>
                  );
                })}
            </div>
          </section>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl}>From</label>
              <select className={inp + " mb-2 h-11"} value={form.from_type} onChange={(e) => { set("from_type", e.target.value); set("from_id", ""); }} data-testid="organise-from-type">
                <option value="project">Project</option>
                <option value="site">Site (depot / hangar)</option>
              </select>
              <select className={inp + " h-11"} value={form.from_id} onChange={(e) => set("from_id", e.target.value)} data-testid="organise-from-dest">
                <option value="">Select an origin</option>
                {(form.from_type === "project" ? projects : sites).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>To <span className="text-red-500">*</span></label>
              <select className={inp + " mb-2 h-11"} value={form.to_type} onChange={(e) => { set("to_type", e.target.value); set("to_id", ""); }} data-testid="organise-to-type">
                <option value="site">Site (depot / hangar)</option>
                <option value="project">Project</option>
              </select>
              <select className={inp + " h-11"} value={form.to_id} onChange={(e) => set("to_id", e.target.value)} data-testid="organise-to-dest">
                <option value="">Select a destination</option>
                {(form.to_type === "project" ? projects : sites).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl}>Scheduled date <span className="font-normal normal-case tracking-normal text-ink-tertiary">(transport date)</span></label>
              <input type="date" className={inp + " h-11"} value={form.scheduled_at} onChange={(e) => set("scheduled_at", e.target.value)} data-testid="organise-date" /></div>
            <div><label className={lbl}>Transported by</label>
              <select className={inp + " h-11"} value={form.carrier} onChange={(e) => set("carrier", e.target.value)} data-testid="organise-carrier">
                <option value="">Not assigned yet</option>
                {carriers.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              {carriers.length === 0 && <p className="text-[11px] text-ink-tertiary mt-1">No transport providers — flag a supplier as "Transport Provider" in Suppliers.</p>}
            </div>
          </div>
          {form.to_type === "project" && (
            <div data-testid="organise-onhire-wrap"><label className={lbl}>On hire date <span className="font-normal normal-case tracking-normal text-ink-tertiary">(becomes the allocation start date)</span></label>
              <input type="date" className={inp + " h-11"} value={form.on_hire_date} onChange={(e) => set("on_hire_date", e.target.value)} data-testid="organise-onhire-date" /></div>
          )}
          <div><label className={lbl}>Notes</label>
            <input className={inp + " h-11"} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="organise-notes" placeholder="Float required, access restrictions, escort" /></div>
        </div>

        <div className="flex items-center justify-between gap-4 px-6 py-4 bg-surface-panel border-t border-border shrink-0">
          <p className="text-[12.5px] text-ink-tertiary">{selected.length === 0 ? "Pick at least one asset to move." : !form.to_id ? "Choose a destination." : `Moving ${selected.length} asset${selected.length === 1 ? "" : "s"}.`}</p>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="h-11 px-5 border border-border-control bg-white text-ink-primary text-[12px] uppercase tracking-[0.05em] font-bold rounded-[10px] hover:bg-surface-panel active:bg-surface-pressed">Cancel</button>
            <button onClick={submit} disabled={saving} data-testid="organise-submit"
              className="h-11 px-6 bg-equipr-yellow text-black text-[12px] uppercase tracking-[0.05em] font-bold rounded-[10px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px disabled:opacity-60 inline-flex items-center gap-1.5">
              <Truck size={15} weight="bold" /> {saving ? "Creating…" : "Create Transport"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
